import { ActivityLevel } from "./quiz/activity";
import { Age } from "./quiz/age";
import { Equipment } from "./quiz/equipment";
import { Experience } from "./quiz/experience";
import { Focus } from "./quiz/focus";
import { Gender } from "./quiz/gender";
import { Goals } from "./quiz/goals";
import { HeightUnit } from "./quiz/height";
import { NumberWorkouts } from "./quiz/numberWorkouts";
import { TimeAvailable } from "./quiz/timeAvailable";
import { WeightUnit } from "./quiz/weight";
import type { User } from "./types";

export const APP_CHECKOUT_ENTRY = "ios_long_pre_paywall_control";
export const SURVEY_STORAGE_KEY = "BWS_SURVEY_DATA";
export const SESSION_IDENTITY_STORAGE_KEY = "BWS_ANSWER_TRACKER_SESSION_ID";

type StorageWriter = Pick<Storage, "setItem">;
type JsonObject = Record<string, unknown>;

export type AppCheckoutUser = User & {
  goalChoice: "loseWeight" | "loseBodyfat" | "buildMuscle";
  availableDays?: string[];
};

export type AppCheckoutEntry = {
  user: AppCheckoutUser;
  sessionIdentityId?: string;
};

const isObject = (value: unknown): value is JsonObject =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const nonEmptyString = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const trimmed = value.trim();
  return trimmed || undefined;
};

const finiteNumber = (value: unknown): number | undefined => {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim()) {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const firstDefined = (...values: unknown[]) =>
  values.find((value) => value !== undefined && value !== null);

function parseDob(value: unknown): Date | undefined {
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? undefined : value;
  }

  const numericValue = finiteNumber(value);
  if (numericValue !== undefined) {
    const milliseconds =
      numericValue < 10_000_000_000 ? numericValue * 1000 : numericValue;
    const date = new Date(milliseconds);
    return Number.isNaN(date.getTime()) ? undefined : date;
  }

  const text = nonEmptyString(value);
  if (!text) return undefined;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? undefined : date;
}

function ageBandFromDob(dob: Date, now: Date): Age | undefined {
  let years = now.getUTCFullYear() - dob.getUTCFullYear();
  const birthdayHasPassed =
    now.getUTCMonth() > dob.getUTCMonth() ||
    (now.getUTCMonth() === dob.getUTCMonth() &&
      now.getUTCDate() >= dob.getUTCDate());
  if (!birthdayHasPassed) years -= 1;
  if (years < 13 || years > 120) return undefined;
  if (years < 20) return Age.Teens;
  if (years < 30) return Age.Twenties;
  if (years < 40) return Age.Thirties;
  if (years < 50) return Age.Fourties;
  return Age.OverFifty;
}

function mapGoalChoice(value: unknown) {
  switch (value) {
    case "loseWeight":
      return { goalChoice: value, goals: Goals.LoseFat } as const;
    case "loseBodyfat":
    case "loseBodyFat":
      return {
        goalChoice: "loseBodyfat",
        goals: Goals.BuildMuscleAndLoseFat,
      } as const;
    case "buildMuscle":
      return { goalChoice: value, goals: Goals.BuildMuscle } as const;
    default:
      return undefined;
  }
}

function mapGender(value: unknown): Gender | undefined {
  switch (nonEmptyString(value)?.toLowerCase()) {
    case "male":
      return Gender.Male;
    case "female":
      return Gender.Female;
    case "non-binary":
    case "nonbinary":
      return Gender.NonBinary;
    default:
      return undefined;
  }
}

function mapExperience(context: JsonObject): Experience | undefined {
  const choice = nonEmptyString(context.experienceChoice)?.toLowerCase();
  if (choice === "advanced") return Experience.Advanced;
  if (choice === "intermediate") return Experience.Intermediate;
  if (["beginner", "novice", "comeback"].includes(choice ?? "")) {
    return Experience.Beginner;
  }

  const level = nonEmptyString(firstDefined(context.level, context.experience));
  if (level === "Intermediate") return Experience.Intermediate;
  if (level === "Advanced") return Experience.Advanced;
  if (
    level === "Beginner Phase 1" ||
    level === "Beginner Phase 2" ||
    level === "Beginner"
  ) {
    return Experience.Beginner;
  }
  return undefined;
}

function mapActivity(
  context: JsonObject,
  customData: JsonObject
): ActivityLevel | undefined {
  const value = nonEmptyString(
    firstDefined(customData.activity, context.activity, context.activityLevel)
  );
  switch (value) {
    case ActivityLevel.Sedentary:
    case "mostlySitting":
      return ActivityLevel.Sedentary;
    case ActivityLevel.SomewhatActive:
    case "low":
      return ActivityLevel.SomewhatActive;
    case ActivityLevel.VeryActive:
    case "medium":
      return ActivityLevel.VeryActive;
    case ActivityLevel.HardlySitStill:
    case "high":
      return ActivityLevel.HardlySitStill;
    default:
      return undefined;
  }
}

function mapEquipment(value: unknown): Equipment | undefined {
  switch (nonEmptyString(value)) {
    case Equipment.FullCommercialGym:
    case "Full Gym":
    case "full":
      return Equipment.FullCommercialGym;
    case Equipment.LimitedGym:
    case "Limited Gym":
    case "limited":
      return Equipment.LimitedGym;
    case Equipment.FullHomeGym:
    case "Home Gym":
    case "home":
      return Equipment.FullHomeGym;
    case Equipment.DumbellsOnly:
    case "Dumbbells Only":
    case "dumbells":
      return Equipment.DumbellsOnly;
    case Equipment.ResistanceBandsOnly:
    case "Resistance Bands Only":
    case "bands":
      return Equipment.ResistanceBandsOnly;
    case Equipment.Bodyweight:
    case "Bodyweight Only":
    case "bodyweight":
      return Equipment.Bodyweight;
    default:
      return undefined;
  }
}

function mapSplit(value: unknown): NumberWorkouts | undefined {
  const split = finiteNumber(value);
  if (split === 2) return NumberWorkouts.Two;
  if (split === 3) return NumberWorkouts.Three;
  if (split === 4) return NumberWorkouts.Four;
  if (split === 5) return NumberWorkouts.Five;
  if (split === 6) return NumberWorkouts.AsManyAsPossible;
  return undefined;
}

function mapWorkoutDuration(context: JsonObject): TimeAvailable | undefined {
  const value = firstDefined(context.durationChoice, context.duration);
  if (
    finiteNumber(value) === 30 ||
    value === "30-Minute" ||
    value === "30OrLess"
  ) {
    return TimeAvailable.ThirtyMinutes;
  }
  if (finiteNumber(value) === 60 || value === "Regular" || value === "60") {
    return TimeAvailable.SixtyMinutes;
  }
  return undefined;
}

function mapHeight(context: JsonObject) {
  const sourceUnit = nonEmptyString(
    firstDefined(context.lengthUnit, context.heightUnit)
  )?.toLowerCase();
  const heightCm = finiteNumber(context.heightCm);
  const heightValue = finiteNumber(
    firstDefined(context.height, context.heightRaw)
  );

  if (sourceUnit === "cm") {
    const centimeters = heightCm ?? heightValue;
    if (centimeters === undefined || centimeters < 90 || centimeters > 250)
      return undefined;
    return {
      heightUnit: HeightUnit.Centimeters,
      heightRaw: Math.round(centimeters),
    };
  }

  if (sourceUnit === "in" || sourceUnit === "ft") {
    const totalInches =
      heightCm !== undefined
        ? Math.round(heightCm / 2.54)
        : sourceUnit === "in"
        ? heightValue
        : heightValue !== undefined
        ? heightValue * 12 + (finiteNumber(context.heightInches) ?? 0)
        : undefined;
    if (totalInches === undefined || totalInches < 36 || totalInches > 99)
      return undefined;
    return {
      heightUnit: HeightUnit.Feet,
      heightRaw: Math.floor(totalInches / 12),
      heightInches: totalInches % 12,
    };
  }

  return undefined;
}

function mapWeight(context: JsonObject) {
  const sourceUnit = nonEmptyString(context.weightUnit)?.toLowerCase();
  const pounds = finiteNumber(context.lbs);
  const sourceWeight = finiteNumber(context.weight);
  if (sourceUnit !== WeightUnit.Pounds && sourceUnit !== WeightUnit.Kilograms)
    return undefined;

  const weight =
    sourceUnit === WeightUnit.Pounds
      ? pounds ?? sourceWeight
      : pounds !== undefined
      ? pounds / 2.20462
      : sourceWeight;
  if (weight === undefined || weight < 25 || weight > 1000) return undefined;
  return {
    weightUnit: sourceUnit as WeightUnit,
    weight: Math.round(weight * 10) / 10,
  };
}

function normalizePercent(value: unknown): number | undefined {
  const number = finiteNumber(value);
  if (number === undefined) return undefined;
  const percent = number <= 1 ? number * 100 : number;
  return percent >= 3 && percent <= 70
    ? Math.round(percent * 10) / 10
    : undefined;
}

function mapAvailableDays(
  context: JsonObject,
  customData: JsonObject
): string[] | undefined {
  const direct = firstDefined(customData.availableDays, context.availableDays);
  if (Array.isArray(direct)) {
    const days = direct
      .map(nonEmptyString)
      .filter((day): day is string => !!day);
    return days.length ? days : undefined;
  }
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ]
    .filter((day) => context[day] === true)
    .map((day) => day.charAt(0).toUpperCase() + day.slice(1));
  return days.length ? days : undefined;
}

export function mapAppCheckoutContext(
  context: unknown,
  now = new Date()
): AppCheckoutUser | null {
  if (!isObject(context)) return null;
  const customData = isObject(context.customData) ? context.customData : {};
  const name = nonEmptyString(
    firstDefined(context.name, context.preferredName)
  );
  const email = nonEmptyString(context.email);
  const goal = mapGoalChoice(context.goalChoice);
  const gender = mapGender(context.gender);
  const dob = parseDob(firstDefined(context.dob, context.dateOfBirth));
  const age = dob ? ageBandFromDob(dob, now) : undefined;
  const height = mapHeight(context);
  const weight = mapWeight(context);
  const bodyFat = normalizePercent(
    firstDefined(context.bodyfat, context.bodyFat)
  );
  const experience = mapExperience(context);
  const activity = mapActivity(context, customData);
  const numberWorkouts = mapSplit(
    firstDefined(context.split, context.numberWorkouts)
  );
  const timeAvailable = mapWorkoutDuration(context);
  const equipment = mapEquipment(
    firstDefined(context.equipmentPreset, context.equipment)
  );

  if (
    !name ||
    !email ||
    !/^\S+@\S+\.\S+$/.test(email) ||
    !goal ||
    !gender ||
    !dob ||
    !age ||
    !height ||
    !weight ||
    bodyFat === undefined ||
    !experience ||
    !activity ||
    !numberWorkouts ||
    !timeAvailable ||
    !equipment
  ) {
    return null;
  }

  const availableDays = mapAvailableDays(context, customData);
  const user = {
    name,
    email,
    ...goal,
    gender,
    age,
    ...height,
    ...weight,
    bodyFat,
    experience,
    activity,
    numberWorkouts,
    timeAvailable,
    equipment,
    duration: nonEmptyString(context.duration) ?? "0-6 months",
    focus: Focus.Training,
    ...(availableDays ? { availableDays } : {}),
  } as AppCheckoutUser;

  if (goal.goalChoice === "loseWeight") {
    const goalPounds = finiteNumber(
      firstDefined(context.goalWeight, customData.goalWeight)
    );
    if (goalPounds === undefined || goalPounds <= 0) return null;
    user.goalWeight =
      weight.weightUnit === WeightUnit.Kilograms
        ? Math.round((goalPounds / 2.20462) * 10) / 10
        : goalPounds;
    if (user.goalWeight >= user.weight) return null;
  } else if (goal.goalChoice === "loseBodyfat") {
    const goalBodyFat = normalizePercent(
      firstDefined(
        context.goalBodyfat,
        context.goalBodyFat,
        customData.goalBodyFat
      )
    );
    if (goalBodyFat === undefined || goalBodyFat >= bodyFat) return null;
    user.goalBodyFat = goalBodyFat;
  } else {
    const muscleGain = finiteNumber(context.muscleGain);
    if (muscleGain === undefined || muscleGain <= 0) return null;
    user.muscleGain = muscleGain;
  }

  return user;
}

export function isAppCheckoutEntry(search: string): boolean {
  return (
    new URLSearchParams(search).get("entry") === APP_CHECKOUT_ENTRY
  );
}

export function parseAppCheckoutEntry(
  search: string,
  now = new Date()
): AppCheckoutEntry | null {
  const params = new URLSearchParams(search);
  if (params.get("entry") !== APP_CHECKOUT_ENTRY) return null;
  const serializedContext = params.get("context");
  if (!serializedContext) return null;

  try {
    const context = JSON.parse(serializedContext);
    const user = mapAppCheckoutContext(context, now);
    if (!user || !isObject(context)) return null;
    const sessionIdentityId = nonEmptyString(
      firstDefined(
        context.sessionIdentityId,
        context.sessionIdentitySessionId,
        context.session_identity_session_id,
        context.sessionId
      )
    );
    return { user, ...(sessionIdentityId ? { sessionIdentityId } : {}) };
  } catch {
    return null;
  }
}

export function hydrateAppCheckoutEntry(
  search: string,
  storage: StorageWriter = localStorage,
  now = new Date()
): AppCheckoutEntry | null {
  const entry = parseAppCheckoutEntry(search, now);
  if (!entry) return null;

  storage.setItem(
    SURVEY_STORAGE_KEY,
    JSON.stringify({ currentPageIndex: 1, data: entry.user })
  );
  if (entry.sessionIdentityId) {
    storage.setItem(SESSION_IDENTITY_STORAGE_KEY, entry.sessionIdentityId);
  }
  return entry;
}

/** Plan the app's checkout lands on when the URL does not name one. */
export const APP_CHECKOUT_DEFAULT_PLAN_ID = "committed";

/** Single-member plans the app's offer coupon applies to. */
const APP_CHECKOUT_OFFER_PLAN_IDS = ["committed", "serious"];

/**
 * The app's own offer: a Stripe coupon id in ?discountId=, applied to the
 * single-user annual (committed) or monthly (serious) trial checkout. The app
 * sends the coupon that matches the plan it opened. Other plans keep control
 * pricing. Core validates the coupon when it creates the session.
 */
export function readAppCheckoutDiscountId(
  search: string,
  plan: { id: string; isMultiUser?: boolean } | undefined
): string | undefined {
  if (
    !isAppCheckoutEntry(search) ||
    !plan ||
    !APP_CHECKOUT_OFFER_PLAN_IDS.includes(plan.id) ||
    plan.isMultiUser
  ) {
    return undefined;
  }
  return nonEmptyString(new URLSearchParams(search).get("discountId"));
}

export type AppCheckoutReturnParams = {
  mode: "webview";
  connectionId: string;
  attemptId?: string;
};

/**
 * The app opens this page in the phone's browser with its realtime connection
 * id and a per-checkout recovery attempt id in the URL. Both must reach the
 * backend on every session the page creates (initial and promo re-creation)
 * so the session returns to the in-app post-purchase page and the app can
 * recover the result. The connection id falls back to storage because App.tsx
 * persists it there after the first render.
 */
export function readAppCheckoutReturnParams(
  search: string,
  storage: Pick<Storage, "getItem"> = localStorage
): AppCheckoutReturnParams | null {
  if (!isAppCheckoutEntry(search)) return null;
  const params = new URLSearchParams(search);
  const connectionId =
    params.get("iacConnectionId") || storage.getItem("iacConnectionId");
  if (!connectionId) return null;
  const attemptId = params.get("iacAttempt") || undefined;
  return { mode: "webview", connectionId, attemptId };
}
